import React, { useState, useEffect } from "react";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface ApplicationHistoryProps {
  candidateName: string;
  screeningId?: string;
  isOpen: boolean;
  onClose: () => void;
}

interface HistoryItem {
  id: string;
  screening_id: string;
  title: string;
  description: string;
  application_type: number;
  member_id: number;
  status: number;
  added_by: number;
  created_at: string;
  updated_at: string;
  added_by_details?: any;
}

export default function ApplicationHistory({ candidateName, screeningId, isOpen, onClose }: ApplicationHistoryProps) {
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("job-details");

  const screeningApi = useScreeningApi();
  const toast = useToaster();

  // Fetch history data when component opens
  useEffect(() => {
    if (isOpen && screeningId) {
      fetchHistoryData();
    }
  }, [isOpen, screeningId]);

  const fetchHistoryData = () => {
    if (!screeningId) {
      console.warn("No screening ID provided");
      return;
    }

    setLoading(true);

    screeningApi.callGetHistory(
      {
        screening_id: screeningId,
      },
      (message: string, resp: any) => {
        console.log("History data fetched successfully:", resp);

        // Handle the API response structure
        if (resp && resp.data && Array.isArray(resp.data)) {
          setHistoryItems(resp.data);
        } else if (resp && Array.isArray(resp)) {
          // In case the response is directly an array
          setHistoryItems(resp);
        } else {
          setHistoryItems([]);
        }

        setLoading(false);
      },
      (error: string) => {
        console.error("Error fetching history data:", error);
        toast.error("Failed to fetch history data: " + error);
        setHistoryItems([]);
        setLoading(false);
      }
    );
  };

  // Helper function to get icon and color based on application_type
  const getHistoryItemStyle = (item: HistoryItem) => {
    switch (item.application_type) {
      case 1:
        return {
          icon: "fa-file-text",
          color: "#3b82f6",
          typeLabel: t("jobDetails.applicationHistory.types.application"),
        };
      case 2:
        return {
          icon: "fa-comment",
          color: "#10b981",
          typeLabel: t("jobDetails.applicationHistory.types.feedback"),
        };
      case 3:
        return {
          icon: "fa-search",
          color: "#8b5cf6",
          typeLabel: t("jobDetails.applicationHistory.types.screening"),
        };
      case 4:
        return {
          icon: "fa-video-camera",
          color: "#f59e0b",
          typeLabel: t("jobDetails.applicationHistory.types.interview"),
        };
      default:
        return {
          icon: "fa-circle",
          color: "#6b7280",
          typeLabel: t("jobDetails.applicationHistory.types.update"),
        };
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="application-history-overlay">
      <div className="application-history-sidebar">
        <div className="history-header">
          <h3>{t("jobDetails.applicationHistory.title")}</h3>
          <p>{t("jobDetails.applicationHistory.subtitle", { name: candidateName })}</p>
          <button className="close-btn" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div className="history-timeline">
          {loading ? (
            <div className="loading-container" style={{ padding: "40px 20px", textAlign: "center" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #3498db",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                <span style={{ fontSize: "16px", color: "#666" }}>{t("jobDetails.applicationHistory.loading")}</span>
              </div>
            </div>
          ) : historyItems.length > 0 ? (
            historyItems.map((item, index) => {
              const style = getHistoryItemStyle(item);
              return (
                <div key={item.id} className="timeline-item">
                  <div className="timeline-icon-wrapper">
                    <div className="timeline-icon" style={{ backgroundColor: style.color }}>
                      <i className={`fa ${style.icon}`}></i>
                    </div>
                    {index < historyItems.length - 1 && <div className="timeline-line"></div>}
                  </div>
                  <div className="timeline-details">
                    <h4 className="timeline-title">{item.title}</h4>
                    <span className="timeline-date">
                      {item?.added_by_details?.name ? `- By ${item.added_by_details.name}` : null}
                    </span>
                    <span className="timeline-date">{formatDate(item.created_at)}</span>
                    <p className="timeline-description">{item.description}</p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-history" style={{ padding: "40px 20px", textAlign: "center", color: "#666" }}>
              <i className="fa fa-clock" style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}></i>
              <p>{t("jobDetails.applicationHistory.noHistory")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
